/**
 * Validates a form before publishing
 * Returns { isValid: boolean, errors: string[] }
 */
export const validateForm = (form) => {
  const errors = [];

  // Check if form has a title
  if (!form.title || form.title.trim() === '' || form.title === 'Untitled Form') {
    errors.push('Please add a title to your form');
  }

  // Check if form has at least one question
  if (!form.questions || form.questions.length === 0) {
    errors.push('Please add at least one question to your form');
  }

  // Validate each question
  form.questions?.forEach((question, index) => {
    // Check if question has text
    if (!question.text || question.text.trim() === '') {
      errors.push(`Question ${index + 1} is missing question text`);
    }

    // Check multiple choice has options
    if (question.type === 'multiple-choice' || question.type === 'checkbox') {
      if (!question.options || question.options.length < 2) {
        errors.push(`Question ${index + 1} needs at least 2 options`);
      }

      // Check for empty options
      const hasEmptyOptions = question.options?.some(opt => !opt || opt.trim() === '');
      if (hasEmptyOptions) {
        errors.push(`Question ${index + 1} has empty options`);
      }
    }

    // Check points are valid (if gamification enabled)
    if (form.settings?.gamification && question.points < 0) {
      errors.push(`Question ${index + 1} has invalid points value`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates a single question
 */
export const validateQuestion = (question) => {
  const errors = [];

  if (!question.text || question.text.trim() === '') {
    errors.push('Question text is required');
  }

  if (question.type === 'multiple-choice' || question.type === 'checkbox') {
    if (!question.options || question.options.length < 2) {
      errors.push('At least 2 options are required');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Checks if form is ready to publish
 */
export const canPublishForm = (form) => {
  const validation = validateForm(form);
  
  // Additional checks for publishing
  if (validation.isValid) {
    // Could add more checks here like:
    // - Has description
    // - All required settings configured
    // - etc.
  }

  return validation;
};

/**
 * Sanitize form data before saving
 */
export const sanitizeFormData = (form) => {
  return {
    ...form,
    title: form.title.trim(),
    description: form.description?.trim() || '',
    questions: form.questions.map(q => ({
      ...q,
      text: q.text.trim(),
      options: q.options?.map(opt => opt.trim()).filter(opt => opt !== '')
    }))
  };
};